import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Textarea } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import {
    AccountModulesDocument,
    DisableAccountModuleDocument,
    EnableAccountModuleDocument,
} from '../../graphql/__generated__/types';
import { ModuleDirection, moduleRefusalOf } from './moduleRefusal';

interface Props {
    accountId: string;
    accountName: string;
    module: { code: string; label: string };
    direction: ModuleDirection;
    onClose: () => void;
}

/**
 * Confirmation of opening and closing a module on an account. A modal rather than a
 * switch because closing is real and immediate (the entry leaves everybody's menu, every
 * reading path starts answering `MODULE_NOT_ENABLED`). Both directions share one
 * component: they act on the same `organization_module` row, creating and deleting it.
 */
const AccountModuleDialog = ({ accountId, accountName, module, direction, onClose }: Props) => {
    const [reason, setReason] = useState('');
    const opening = direction === 'enable';

    // Both mutations answer with the whole referential; `AccountModule` carries no `id`,
    // so Apollo normalises none of these objects and we must write the query by hand
    // (same selection set and variables, no round trip).
    const [enable, enableState] = useMutation(EnableAccountModuleDocument, {
        update: (cache, { data }) => {
            if (data?.enableAccountModule) {
                cache.writeQuery({
                    query: AccountModulesDocument,
                    variables: { organizationId: accountId },
                    data: { accountModules: data.enableAccountModule },
                });
            }
        },
    });
    const [disable, disableState] = useMutation(DisableAccountModuleDocument, {
        update: (cache, { data }) => {
            if (data?.disableAccountModule) {
                cache.writeQuery({
                    query: AccountModulesDocument,
                    variables: { organizationId: accountId },
                    data: { accountModules: data.disableAccountModule },
                });
            }
        },
    });

    const { loading, error } = opening ? enableState : disableState;
    const done = opening ? enableState.data?.enableAccountModule : disableState.data?.disableAccountModule;

    const submit = () => {
        const run = opening
            ? enable({ variables: { organizationId: accountId, moduleCode: module.code } })
            : disable({ variables: { organizationId: accountId, moduleCode: module.code, reason: reason.trim() } });
        // The refusal is read off `error` below; without this the rejected promise
        // reaches the console as an unhandled one on every single refusal.
        run.catch(() => {});
    };

    const title = opening
        ? c('Account module').t`Open this module on the account`
        : c('Account module').t`Close this module on the account`;

    // A motive is required on screen although the API takes it as optional: it is the
    // only trace of *why* a client stopped seeing a whole section of the portal.
    const blocked = !opening && reason.trim().length === 0;

    return (
        <Modal open onClose={onClose} title={title} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{module.label}</b>
                        <span className="text-text-600"> · {accountName}</span>
                    </p>

                    {done ? (
                        <Alert type="success">
                            {opening
                                ? c('Account module')
                                      .t`The module is open on this account. Nobody gained a right by it: who reaches it is still decided by the roles, and it opened nothing on the child accounts — each one is opened for itself.`
                                : c('Account module')
                                      .t`The module is closed on this account. Its entry has already left the menu of everybody attached to it, and every reading of it now answers that the module is not open — explicitly, never as an empty page.`}
                        </Alert>
                    ) : (
                        <>
                            {/* Said before the button, not after: a warning read on the
                                confirmation comes too late. */}
                            <Alert type="warning">
                                {opening
                                    ? c('Account module')
                                          .t`Opening grants a right to nobody. It opens the module on this account; who actually reaches it is decided by the roles, and a right granted where the module is closed stays granted and without effect. Nothing descends the tree either: the child accounts are unaffected.`
                                    : c('Account module')
                                          .t`Closing removes the module from this account, and it closes for real: the entry disappears from the menu of everybody attached to it, and every reading path of the module starts refusing. Opening it again later is one click, but what people saw meanwhile they will stop seeing at once.`}
                            </Alert>

                            {!opening && (
                                <div>
                                    <label
                                        htmlFor="account-module-reason"
                                        className="mb-1.5 block text-sm font-medium text-text-900"
                                    >
                                        {c('Account module').t`Reason for closing`}
                                    </label>
                                    <Textarea
                                        id="account-module-reason"
                                        value={reason}
                                        onValue={setReason}
                                        placeholder={c('Account module')
                                            .t`Opened by mistake, scope of the contract changed…`}
                                    />
                                    <p className="mt-1.5 text-xs text-text-600">
                                        {c('Account module')
                                            .t`It is journalled with the gesture, and it is the only trace of why this account stopped seeing this module.`}
                                    </p>
                                </div>
                            )}

                            {/* The refusal the screen could not pre-empt. */}
                            {error && <RefusalNotice refusal={moduleRefusalOf(error, direction)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    // « Terminé » and not « Fermer »: on this dialog of all places,
                    // « Fermer » is the name of the other gesture.
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button
                            // `destructive` for closing only: opening is consequential, not
                            // destructive.
                            category={opening ? 'default' : 'destructive'}
                            loading={loading}
                            disabled={blocked}
                            onClick={submit}
                        >
                            {opening
                                ? c('Account module action').t`Open the module`
                                : c('Account module action').t`Close the module`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default AccountModuleDialog;
